import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Drawer,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import UserMin from '../plans/UserMin';
import MagicRecipeBuilder from './MagicRecipeBuilder';
import WeekPlanTable from './WeekPlanTable';
import nutritionService from '../../services/nutritionService';

// Dictionnaire pour la traduction
const mealTranslations = {
  breakfast: 'Petit-déjeuner',
  morningSnack: 'Encas matin',
  lunch: 'Déjeuner',
  snack: 'Encas journée',
  dinner: 'Dîner',
};

const calculateDayCalories = async (dayPlan, clientId) => {
  // Récupérer toutes les recettes du jour avec ingrédients
  const dayRecipes = Object.values(dayPlan.meals).filter(meal => meal && meal.ingredients);

  if (dayRecipes.length === 0) {
    return 0;
  }

  try {
    // V2.0: Utiliser directement l'API nutritionService
    const result = await nutritionService.adjustDayRecipes(
      dayRecipes,
      clientId,
      dayPlan.dayType || 'OFF'
    );

    // Utiliser dayTotals.calories si disponible
    if (result.dayTotals && result.dayTotals.calories !== undefined) {
      return Math.round(result.dayTotals.calories);
    }

    // Fallback: calculer depuis les recettes ajustées
    if (result.recipes && Array.isArray(result.recipes)) {
      const totalCalories = result.recipes.reduce((sum, recipe) => {
        return sum + (recipe.macros?.calories || 0);
      }, 0);
      return Math.round(totalCalories);
    }

    return 0;
  } catch (error) {
    console.error('Erreur calcul calories:', error);
    return 0;
  }
};

// V2.0: Cette fonction est maintenant un getter depuis les macros calculées par l'API
// Elle est appelée avec clientMacros en paramètre additionnel
const getTargetCaloriesForDay = (dayType, clientMacros) => {
  if (!clientMacros) {
    return null;
  }

  // L'API calculateAllMacros retourne { OFF: {...}, ON: {...}, INTENSIF: {...} }
  // Chaque type contient { daily: { totalCal, ... }, perMeal: {...} }
  const macrosForDay = clientMacros[dayType] || clientMacros['OFF'];

  if (macrosForDay?.daily?.totalCal) {
    return Math.round(macrosForDay.daily.totalCal);
  }

  return null;
};


// Fonction utilitaire pour traduire une clé anglaise en français
const translateMealToFrench = (mealKey) => mealTranslations[mealKey] || mealKey;

const CATEGORY_TRANSLATIONS = {
  vegetarian: "Végétarien",
  vegan: "Vegan",
  "gluten-free": "Sans gluten",
  "dairy-free": "Sans produits laitiers",
  paleo: "Paléo",
  keto: "Kéto",
  other: "Autre",
};


const getMealKeyFromFrench = (frenchMeal) => {
  const normalizedFrenchMeal = frenchMeal.trim().toLowerCase();

  // Cas particulier pour morningSnack
  if (normalizedFrenchMeal === 'encas matin') {
    return 'morningSnack';
  }

  const key = Object.keys(mealTranslations).find(
    (key) => mealTranslations[key].toLowerCase() === normalizedFrenchMeal
  );

  return key || frenchMeal.toLowerCase();
};

const RecipeSelection = ({
  weekNumber,
  setWeekNumber,
  dayPlans,
  setDayPlans,
  handleDayPlanChange,
  recipes = [],
  userPreferences,
  selectedClient,
  nextStep,
  prevStep,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [calculatedCalories, setCalculatedCalories] = useState({});
  const [loadingCalories, setLoadingCalories] = useState({});
  const [isInitialCalculationDone, setIsInitialCalculationDone] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [clientMacros, setClientMacros] = useState(null); // V2.0: Macros calculées par l'API

const recalculateCaloriesForDay = async (index, day) => {
  setLoadingCalories((prev) => ({ ...prev, [index]: true }));

  const hasMeals = Object.values(day.meals || {}).some(meal => meal && meal.ingredients);
  let calories = 0;

  if (hasMeals && selectedClient?._id) {
    calories = await calculateDayCalories(day, selectedClient._id);
  }

  setCalculatedCalories((prev) => ({ ...prev, [index]: calories }));
  setLoadingCalories((prev) => ({ ...prev, [index]: false }));
};

useEffect(() => {
  if (!selectedClient?._id) return;

  const initializeData = async () => {
    // V2.0: Charger les macros cibles du client via l'API
    try {
      const macrosData = await nutritionService.calculateAllMacros(selectedClient._id);
      if (macrosData?.macros) {
        setClientMacros(macrosData.macros);
      }
    } catch (err) {
      console.error('Erreur chargement macros client:', err);
    }

    // Calculer les calories de chaque jour
    const newTotals = {};

    for (let i = 0; i < dayPlans.length; i++) {
      setLoadingCalories((prev) => ({ ...prev, [i]: true }));

      const day = dayPlans[i];
      const hasMeals = Object.values(day.meals || {}).some(meal => meal && meal.ingredients);

      if (hasMeals) {
        newTotals[i] = await calculateDayCalories(day, selectedClient._id);
      } else {
        newTotals[i] = 0;
      }

      setCalculatedCalories((prev) => ({ ...prev, [i]: newTotals[i] }));
      setLoadingCalories((prev) => ({ ...prev, [i]: false }));
    }

    setIsInitialCalculationDone(true);
  };

  initializeData();
// eslint-disable-next-line
}, [selectedClient]);


  useEffect(() => {
    if (selectedClient) {
      // console.log('Selected Client:', selectedClient);
    }
    if (userPreferences) {
      // console.log('User Preferences:', userPreferences);
    }
    // console.log('dayPlans updated:', dayPlans);
    // console.log('recipes updated:', recipes);
  }, [selectedClient, userPreferences, dayPlans, recipes]);

  const openDrawer = (dayIndex, meal) => {
    console.log('Opening drawer for meal:', meal);
    setSelectedDay(dayIndex);
    setSelectedMeal(meal);
    setDrawerOpen(true);
  };
  
  const closeDrawer = () => {
    setDrawerOpen(false);
    setSearchTerm('');
    setCategoryFilter('');
  };

const handleRemoveMeal = (dayIndex, mealKey) => {
  if (!isInitialCalculationDone) {
    setShowAlert(true);
    return;
  }

  const updatedDayPlans = [...dayPlans];
  if (updatedDayPlans[dayIndex]?.meals[mealKey]) {
    delete updatedDayPlans[dayIndex].meals[mealKey];
  }

  setDayPlans(updatedDayPlans);
  recalculateCaloriesForDay(dayIndex, updatedDayPlans[dayIndex]);
};

const handleDayPlanChangeAndClose = (dayIndex, meal, recipeId) => {
  if (!isInitialCalculationDone) {
    setShowAlert(true);
    return;
  }

  const mealKey = meal;
  const updatedDayPlans = [...dayPlans];

  if (!updatedDayPlans[dayIndex].meals) {
    updatedDayPlans[dayIndex].meals = {};
  }

  const selectedRecipe = recipes.find((recipe) => recipe._id === recipeId);

  // Structure correcte avec source pour le backend + donnees completes pour l'affichage
  updatedDayPlans[dayIndex].meals[mealKey] = {
    recipeId: recipeId,
    source: selectedRecipe?.source || 'coach',
    recipeName: selectedRecipe?.name,
    servingMultiplier: 1,
    // Donnees completes pour l'affichage et le calcul des calories
    _id: recipeId,
    ...selectedRecipe,
  };

  setDayPlans(updatedDayPlans);
  recalculateCaloriesForDay(dayIndex, updatedDayPlans[dayIndex]);
  closeDrawer();
};


  
  const filterRecipes = (meal, searchTerm, categoryFilter) => {
  
    if (!meal) return [];
    const mealKey = getMealKeyFromFrench(meal);
  
    const effectiveMealKeys =
    mealKey.toLowerCase() === 'morningsnack' ? ['snack'] :
    mealKey === 'lunch' ? ['lunch', 'dinner'] :
    mealKey === 'dinner' ? ['dinner', 'lunch'] :
    mealKey === 'snack' ? ['snack', 'breakfast'] :
    mealKey === 'breakfast' ? ['breakfast', 'snack'] : [mealKey];
  
  
    let filtered = recipes.filter((recipe) =>
      effectiveMealKeys.includes(recipe.mealType?.toLowerCase())
    );
    
  
    filtered = filtered.filter((recipe) =>
      recipe.name?.toLowerCase().includes(searchTerm.toLowerCase() || '')
    );
  
    filtered = filtered.filter((recipe) =>
      categoryFilter ? recipe.category.includes(categoryFilter) : true
    );
  
    filtered = filtered.filter((recipe) => {
      if (filters.intolerances && selectedClient?.intolerances?.length > 0) {
        const hasIntolerance = recipe.intolerances.some((intol) =>
          selectedClient.intolerances.includes(intol)
        );
        return !hasIntolerance;
      }
      return true;
    });
  
    filtered = filtered.filter((recipe) => {
      if (filters.dietaryPreferences && userPreferences?.length > 0) {
        return userPreferences.some((preference) =>
          recipe.category.includes(preference)
        );
      }
      return true;
    });
  
    return filtered;
  };
  
  
  const [filters, setFilters] = useState({
    dietaryPreferences: true,
    intolerances: true,
  });

const autoFillRecipes = async () => {
  if (!isInitialCalculationDone) {
    setShowAlert(true);
    return;
  }

  // Liste fixe de toutes les cles de repas
  const MEAL_KEYS = ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner'];

  const updatedDayPlans = dayPlans.map((dayPlan) => {
    const updatedMeals = { ...dayPlan.meals };

    // Iterer sur toutes les cles de repas, pas seulement celles existantes
    MEAL_KEYS.forEach((mealKey) => {
      // Utiliser la traduction francaise pour filterRecipes
      const frenchMeal = mealTranslations[mealKey];
      const filteredRecipes = filterRecipes(frenchMeal, '', categoryFilter);

      if (filteredRecipes.length > 0) {
        const randomIndex = Math.floor(Math.random() * filteredRecipes.length);
        const selectedRecipe = filteredRecipes[randomIndex];

        // Structure correcte avec source pour le backend
        updatedMeals[mealKey] = {
          recipeId: selectedRecipe._id,
          source: selectedRecipe.source || 'coach',
          recipeName: selectedRecipe.name,
          servingMultiplier: 1,
          _id: selectedRecipe._id,
          ...selectedRecipe,
        };
      }
    });

    return { ...dayPlan, meals: updatedMeals };
  });

  setDayPlans(updatedDayPlans);

  // Recalcul des calories apres remplissage
  for (let i = 0; i < updatedDayPlans.length; i++) {
    await recalculateCaloriesForDay(i, updatedDayPlans[i]);
  }
};


  const handleDayTypeChange = async (dayIndex, dayType) => {
    const updatedDayPlans = [...dayPlans];
    updatedDayPlans[dayIndex].dayType = dayType;
    setDayPlans(updatedDayPlans);

    // Recalculer les calories pour ce jour après changement de type
    await recalculateCaloriesForDay(dayIndex, updatedDayPlans[dayIndex]);
  };
  
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

  return (
    <>

      <UserMin selectedClient={selectedClient} userPreferences={userPreferences} />

      <MagicRecipeBuilder
        filters={filters}
        setFilters={setFilters}
        autoFillRecipes={autoFillRecipes}
      />


{/* Vue tableau du planning hebdomadaire */}
      <Box sx={{ mb: 4 }}>
        <WeekPlanTable
          dayPlans={dayPlans}
          setDayPlans={setDayPlans}
          recipes={recipes}
          selectedClient={selectedClient}
          calculatedCalories={calculatedCalories}
          loadingCalories={loadingCalories}
          getTargetCaloriesForDay={(dayType) => getTargetCaloriesForDay(dayType, clientMacros)}
          onMealClick={openDrawer}
          onMealRemove={handleRemoveMeal}
          onDayTypeChange={handleDayTypeChange}
        />
      </Box>

<Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
  <Box sx={{ width: 400, padding: 3 }}>
    {/* Affichage du titre dans le Drawer */}
    <Typography variant="h6" sx={{ marginBottom: 2 }}>
      Recette pour {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'][selectedDay]} - {translateMealToFrench(selectedMeal)}
    </Typography>

    {/* Champ de recherche */}
    <TextField
      label="Rechercher"
      type="text"
      fullWidth
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{ marginBottom: 2 }}
    />

    {/* Liste des recettes filtrées */}
    <List>
      {filterRecipes(selectedMeal, searchTerm, categoryFilter).map((recipe) => (
        <ListItem key={recipe._id} disablePadding>
          <ListItemButton onClick={() => handleDayPlanChangeAndClose(selectedDay, selectedMeal, recipe._id)}>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  {recipe.name}
                  <Chip
                    label={recipe.category.map(cat => CATEGORY_TRANSLATIONS[cat] || cat).join(", ")}
                    size="small"
                    sx={{ marginLeft: 1 }}
                  />
                </Box>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  </Box>
</Drawer>

<Snackbar
  open={showAlert}
  autoHideDuration={3000}
  onClose={() => setShowAlert(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setShowAlert(false)} severity="warning" sx={{ width: '100%' }}>
    Attendez que le programme soit chargé avant de modifier les repas.
  </Alert>
</Snackbar>

      <Box
        sx={{
          display: 'flex',
          justifyContent: window.location.pathname.includes('edit') ? 'flex-end' : 'space-between',
        }}
      >
        {!window.location.pathname.includes('edit') && (
          <Button variant="contained" color="secondary" onClick={prevStep} sx={{ marginRight: 2 }}>
            Retour
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={nextStep}>
          Suivant
        </Button>
      </Box>

    </>
  );
};

export default RecipeSelection;
